// src/components/desk/RoadMarkPicker.jsx — T39: mark the road (or a place) a plot sits on.
//
// OWNER: "mark the road on the map, mark pins, use this location." For land, where the advert's
// text is the only source of "how far from the road" today. Idmon reads `road_distance` out of
// the advert; this is the agent's own statement of WHERE the road is, and the distance follows
// from it (property_road_distance_m, derived at read — 0847).
//
// Two tools, one map: ROAD draws a line click by click (the ManualClientModal search-area
// pattern — click to add, Undo removes the last point, Save sends), PLACE drops one point.
// Saved marks are drawn in their own colour and can be retracted; nothing is edited in place.
//
// ⚠️ The distance shown is the SERVER's number, never computed here. The map only knows
// pixels; the plot's pin can move under it (the address arc moves pins) and the same rule must
// answer on the card, in the claim and here. One definition, in SQL.
import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import { t, tLog } from '../../lib/i18n.js'
// IconText takes an emoji-prefixed STRING and String()s its children — an <Icon/> child rendered
// as "[object Object], ," on production (2026-09-03). Compose icon + text by hand here.
import { Icon } from '../Icon.jsx'
import { btn } from './primitives.jsx'
import { getPropertyMarks, addPropertyMark, retractPropertyMark } from '../../db/core.js'

const ROAD_COLOUR = '#f59e0b', PLACE_COLOUR = '#38bdf8', DRAFT_COLOUR = '#a855f7'

export function RoadMarkPicker({ propertyId, agentId, lat, lng }) {
  const mapEl = useRef(null), mapRef = useRef(null)
  const savedLayerRef = useRef(null), draftLayerRef = useRef(null)
  const [marks, setMarks] = useState(null)          // { marks: [...], road_distance_m }
  const [mode, setMode] = useState('pan')           // 'pan' | 'road' | 'place'
  const [points, setPoints] = useState([])          // draft, [lat, lng]
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [refresh, setRefresh] = useState(0)

  useEffect(() => {
    if (!propertyId) return
    let gone = false
    getPropertyMarks(propertyId)
      .then(d => { if (!gone) { setMarks(d); setErr(null) } })
      .catch(e => { if (!gone) setErr(e.message) })
    return () => { gone = true }
  }, [propertyId, refresh])

  // The map, once.
  useEffect(() => {
    if (mapRef.current || !mapEl.current) return
    const map = L.map(mapEl.current).setView([lat ?? 41.715, lng ?? 44.79], lat != null ? 16 : 12)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OSM' }).addTo(map)
    if (lat != null && lng != null) L.marker([lat, lng]).addTo(map)
    mapRef.current = map
    setTimeout(() => map.invalidateSize(), 50)
  }, [])   // eslint-disable-line react-hooks/exhaustive-deps

  // Click → add a point, in draw modes only. Read `mode` through a ref so the one listener
  // registered at mount sees the current mode rather than the one it closed over.
  const modeRef = useRef(mode); modeRef.current = mode
  useEffect(() => {
    const map = mapRef.current; if (!map) return
    const onClick = e => {
      const m = modeRef.current
      if (m === 'road') setPoints(p => [...p, [e.latlng.lat, e.latlng.lng]])
      else if (m === 'place') setPoints([[e.latlng.lat, e.latlng.lng]])
    }
    map.on('click', onClick)
    return () => map.off('click', onClick)
  }, [])

  // Draft layer follows the points.
  useEffect(() => {
    const map = mapRef.current; if (!map) return
    if (draftLayerRef.current) { draftLayerRef.current.remove(); draftLayerRef.current = null }
    if (!points.length) return
    draftLayerRef.current = points.length > 1
      ? L.polyline(points, { color: DRAFT_COLOUR, weight: 4, dashArray: '6 6' }).addTo(map)
      : L.circleMarker(points[0], { color: DRAFT_COLOUR, radius: 7 }).addTo(map)
  }, [points])

  // Saved marks, redrawn whenever the list changes.
  useEffect(() => {
    const map = mapRef.current; if (!map) return
    if (savedLayerRef.current) { savedLayerRef.current.remove(); savedLayerRef.current = null }
    const group = L.layerGroup()
    for (const m of marks?.marks || []) {
      const g = m.geojson
      if (!g) continue
      if (m.kind === 'road' && g.type === 'LineString') {
        L.polyline(g.coordinates.map(([x, y]) => [y, x]), { color: ROAD_COLOUR, weight: 5, opacity: 0.9 })
          .bindTooltip(m.label || t('desk_mark_road')).addTo(group)
      } else if (m.kind === 'place' && g.type === 'Point') {
        L.circleMarker([g.coordinates[1], g.coordinates[0]], { color: PLACE_COLOUR, radius: 8, fillOpacity: 0.6 })
          .bindTooltip(m.label || t('desk_mark_place')).addTo(group)
      }
    }
    group.addTo(map)
    savedLayerRef.current = group
  }, [marks])

  const canSave = mode === 'road' ? points.length >= 2 : mode === 'place' ? points.length === 1 : false

  async function save() {
    if (!canSave || busy) return
    setBusy(true); setErr(null)
    try {
      const geojson = mode === 'road'
        ? { type: 'LineString', coordinates: points.map(([la, ln]) => [ln, la]) }
        : { type: 'Point', coordinates: [points[0][1], points[0][0]] }
      await addPropertyMark(propertyId, agentId, { kind: mode, geojson, label: label.trim() || null })
      setPoints([]); setLabel(''); setMode('pan'); setRefresh(r => r + 1)
    } catch (e) { setErr(e.message) }
    finally { setBusy(false) }
  }

  async function retract(id) {
    setBusy(true); setErr(null)
    try { await retractPropertyMark(id, agentId); setRefresh(r => r + 1) }
    catch (e) { setErr(e.message) }
    finally { setBusy(false) }
  }

  const dist = marks?.road_distance_m
  return (
    <div className="space-y-2" data-road-mark-picker>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <button type="button" className={btn(mode === 'road' ? 'primary' : 'ghost')} data-mark-mode="road"
          onClick={() => { setMode(m => m === 'road' ? 'pan' : 'road'); setPoints([]) }}>
          <span className="inline-flex items-center gap-1.5"><Icon name="minus" size={13} />{t('desk_mark_draw_road')}</span>
        </button>
        <button type="button" className={btn(mode === 'place' ? 'primary' : 'ghost')} data-mark-mode="place"
          onClick={() => { setMode(m => m === 'place' ? 'pan' : 'place'); setPoints([]) }}>
          <span className="inline-flex items-center gap-1.5"><Icon name="map-pin" size={13} />{t('desk_mark_drop_place')}</span>
        </button>
        {mode === 'road' && points.length > 0 && (
          <button type="button" className={btn('ghost')} onClick={() => setPoints(p => p.slice(0, -1))}>{t('desk_map_undo_point')}</button>
        )}
        <span className="text-muted-foreground">
          {mode === 'pan' ? t('desk_mark_hint_pan')
            : mode === 'road' ? (points.length < 2 ? t('desk_mark_hint_road') : tLog('desk_mark_points_progress', { n: points.length }))
            : (points.length ? t('desk_mark_hint_place_set') : t('desk_mark_hint_place'))}
        </span>
      </div>

      <div ref={mapEl} className="h-64 w-full rounded-lg border border-border overflow-hidden" />

      {mode !== 'pan' && (
        <div className="flex items-center gap-2">
          <input className="flex-1 rounded-lg border border-input bg-background px-2 py-1 text-xs"
            placeholder={t('desk_mark_label_ph')} value={label} onChange={e => setLabel(e.target.value)} />
          <button type="button" className={btn('primary')} disabled={!canSave || busy} data-mark-save onClick={save}>
            {t('desk_mark_save')}
          </button>
        </div>
      )}

      {/* The number that matters, and it is the SERVER's. "—" when there is no pin or no road:
          a plot without a pin has no distance, and saying 0 would be a lie in metres. */}
      <div className="text-sm" data-road-distance={dist ?? ''}>
        <span className="text-muted-foreground">{t('desk_mark_road_distance')}: </span>
        <span className="font-semibold tabular-nums">{dist != null ? `${dist} m` : '—'}</span>
        {dist == null && (marks?.marks || []).some(m => m.kind === 'road') && lat == null && (
          <span className="ml-2 text-xs text-muted-foreground">{t('desk_mark_no_pin')}</span>
        )}
      </div>

      {(marks?.marks || []).length > 0 && (
        <ul className="text-xs space-y-1">
          {marks.marks.map(m => (
            <li key={m.id} className="flex items-center gap-2" data-mark={m.id}>
              <span className="inline-block w-2 h-2 rounded-full" style={{ background: m.kind === 'road' ? ROAD_COLOUR : PLACE_COLOUR }} />
              <span>{m.kind === 'road' ? t('desk_mark_road') : t('desk_mark_place')}{m.label ? ` · ${m.label}` : ''}</span>
              <button type="button" className="text-muted-foreground hover:text-destructive" disabled={busy}
                data-mark-retract={m.id} onClick={() => retract(m.id)}>{t('desk_mark_retract')}</button>
            </li>
          ))}
        </ul>
      )}
      {err && <p className="text-xs text-destructive"><Icon name="alert-triangle" /> {err}</p>}
    </div>
  )
}
